// Super Admin API client.
//
// Talks to /api/superadmin/*, which is gated server-side by requireSuperadmin.
// The token comes from the panel's own session (see SuperAdmin.jsx) — it is a
// normal login token whose user carries role='superadmin'.

const API_URL = import.meta.env?.VITE_API_URL || 'https://api.customfieldpros.com'

function token() {
  try {
    const s = JSON.parse(sessionStorage.getItem('customsfieldpro_sa_session_v2') || 'null')
    return s?.token || null
  } catch { return null }
}

async function call(method, path, body) {
  const t = token()
  if (!t) throw new Error('Your session has expired. Please sign in again.')

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })

  if (res.status === 401) throw new Error('Your session has expired. Please sign in again.')
  if (res.status === 404 && path.startsWith('/api/superadmin')) {
    throw new Error('This account no longer has super admin access.')
  }

  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`)
  return data
}

// ── Reads ─────────────────────────────────────────────────────────────────────
export const fetchTenants = ()  => call('GET', '/api/superadmin/tenants')
export const fetchAdmins  = ()  => call('GET', '/api/superadmin/admins')
export const fetchUsers   = ()  => call('GET', '/api/superadmin/users')
export const fetchStats   = ()  => call('GET', '/api/superadmin/stats')

// ── Writes ────────────────────────────────────────────────────────────────────
export const createAdmin      = (payload)     => call('POST', '/api/superadmin/admins', payload)
export const updateAdmin      = (id, patch)   => call('PUT', `/api/superadmin/admins/${id}`, patch)
export const setAdminActive   = (id, active)  => updateAdmin(id, { is_active: active })
export const resetAdminPassword = (id, password) => updateAdmin(id, { password })
export const updateTenant     = (id, patch)   => call('PUT', `/api/superadmin/tenants/${id}`, patch)
export const setTenantActive  = (id, active)  => updateTenant(id, { is_active: active })

// ── Shape adapter ─────────────────────────────────────────────────────────────
// The panel's JSX was written against the old mock shape. Translating here keeps
// that markup untouched. Fields the database genuinely cannot answer yet are
// left null so the UI can show "—" rather than inventing a number.

const PLAN_MRR = { starter: 49, professional: 99, business: 199, enterprise: 399, trial: 0 }
const PLAN_SEATS = { starter: 3, professional: 10, business: 25, enterprise: 100, trial: 3 }

export function toPanelTenant(t) {
  const owner = t.admins?.[0]
  return {
    id:         t.id,
    name:       t.name,
    email:      t.email || owner?.email || '',
    owner:      owner?.full_name || owner?.email || '— no admin —',
    ownerId:    owner?.id || null,
    admins:     t.admins || [],
    plan:       t.plan || 'trial',
    status:     t.is_active === false ? 'Suspended' : 'Active',
    isActive:   t.is_active !== false,
    users:      t.userCount ?? 0,
    usersLimit: PLAN_SEATS[t.plan] ?? 10,
    jobs:       t.jobCount ?? 0,
    clients:    t.clientCount ?? 0,
    invoices:   t.invoiceCount ?? 0,
    // Headcount by role and the company's own invoiced revenue — all measured
    // from the database, unlike the plan-derived mrr below.
    adminCount:      t.adminCount ?? 0,
    staffCount:      t.staffCount ?? 0,
    technicianCount: t.technicianCount ?? 0,
    invoicedTotal:   t.invoicedTotal ?? 0,
    paidTotal:       t.paidTotal ?? 0,
    mrr:        PLAN_MRR[t.plan] ?? 0,
    joined:     t.created_at ? String(t.created_at).slice(0, 10) : '',
    // Not derivable from the current schema — null renders as "—"
    trialEnd:   null,
    renewal:    null,
    lastActive: null,
    onboarding: null,
    churnScore: null,
  }
}
